import type {
  AnonymousSessionRequest,
  AnonymousSessionResponse,
  AnonymousSessionService,
} from '@/core/infrastructure/services/AnonymousSessionService';

export interface SessionInfo {
  isActive: boolean;
  sessionId: string | null;
  timeUntilExpiry: number;
  sessionDuration: number;
}

export class AnonymousSessionUseCase {
  constructor(
    private readonly anonymousSessionService: AnonymousSessionService
  ) {}

  async createAnonymousSession(
    request?: Partial<AnonymousSessionRequest>
  ): Promise<AnonymousSessionResponse> {
    return await this.anonymousSessionService.createAnonymousSession(request);
  }

  getCurrentSession(): AnonymousSessionResponse | null {
    return this.anonymousSessionService.getCurrentSession();
  }

  getAnonymousToken(): string | null {
    return this.anonymousSessionService.getAnonymousToken();
  }

  isSessionExpired(): boolean {
    return this.anonymousSessionService.isSessionExpired();
  }

  isSessionActive(): boolean {
    return this.anonymousSessionService.isSessionActive();
  }

  async refreshSession(): Promise<AnonymousSessionResponse> {
    return await this.anonymousSessionService.refreshSession();
  }

  async ensureValidSession(): Promise<string> {
    return await this.anonymousSessionService.ensureValidSession();
  }

  clearSession(): void {
    this.anonymousSessionService.clearSession();
  }

  getSessionInfo(): SessionInfo {
    return this.anonymousSessionService.getSessionInfo();
  }

  getSessionDuration(): number {
    return this.anonymousSessionService.getSessionDuration();
  }

  getTimeUntilExpiry(): number {
    return this.anonymousSessionService.getTimeUntilExpiry();
  }

  // Helper methods for guest user workflows
  async ensureGuestAccess(): Promise<string> {
    if (!this.isSessionActive()) {
      const session = await this.createAnonymousSession();
      return session.anonymous_token;
    }
    return this.getAnonymousToken()!;
  }

  async extendSessionIfNeeded(): Promise<void> {
    const timeUntilExpiry = this.getTimeUntilExpiry();
    const tenMinutes = 10 * 60 * 1000; // 10 minutes in milliseconds

    // Refresh if session expires within 10 minutes
    if (timeUntilExpiry < tenMinutes && timeUntilExpiry > 0) {
      await this.refreshSession();
    }
  }

  // Check if user has been browsing for a certain duration (for engagement tracking)
  hasBeenActiveFor(minutes: number): boolean {
    const sessionDuration = this.getSessionDuration();
    const targetDuration = minutes * 60 * 1000; // Convert to milliseconds
    return sessionDuration >= targetDuration;
  }

  // Get session age in minutes
  getSessionAgeInMinutes(): number {
    const sessionDuration = this.getSessionDuration();
    return Math.floor(sessionDuration / (60 * 1000));
  }
}
